import {
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  WebAuthnAbortService,
  WebAuthnError,
} from '@simplewebauthn/browser'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useEffect, useEffectEvent, useState } from 'react'

import {
  exchangePasskey,
  openPasskeySession,
  requestPasskeyChallenge,
  type PasskeySignInResponse,
} from './passkeys'

const DEFAULT_LIFETIME_S = 300
const REFRESH_EARLY_MS = 15_000
const RETRY_MS = 30_000
const MAX_FAILURES = 3

export interface AutofillRun {
  stopped: boolean
  refreshing: boolean
  timer?: ReturnType<typeof setTimeout>
}

export type AutofillStep = 'again' | 'later' | 'stop'

/**
 * What the loop does once a waiting request ends without a sign-in.
 *
 * A refresh asks again at once. Any other abort means the page started the
 * passkey prompt itself (the button, or a password the API refused), which
 * takes over. A refused passkey is shown and the loop waits for the next pick.
 * Anything else is retried later, a few times at most.
 */
export function nextAutofillStep(error: unknown, run: AutofillRun): AutofillStep {
  if (run.stopped) return 'stop'
  if (run.refreshing) return 'again'
  if (error instanceof WebAuthnError && error.code === 'ERROR_CEREMONY_ABORTED') return 'stop'
  if (isAxiosError(error) && error.response?.status === 401) return 'again'
  return 'later'
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function whenVisible(): Promise<void> {
  if (document.visibilityState === 'visible') return Promise.resolve()
  return new Promise((resolve) => {
    const onChange = () => {
      if (document.visibilityState !== 'visible') return
      document.removeEventListener('visibilitychange', onChange)
      resolve()
    }
    document.addEventListener('visibilitychange', onChange)
  })
}

async function waitForPick(run: AutofillRun): Promise<PasskeySignInResponse> {
  const challenge = await requestPasskeyChallenge()
  // A run stopped while its challenge was on the way must not start a request
  // that would cancel the one its successor (or the button) just opened.
  if (run.stopped) throw new Error('autofill stopped')
  // An API that predates `expires_in` keeps its challenges for five minutes.
  const lifetime = (challenge.expires_in || DEFAULT_LIFETIME_S) * 1000
  run.timer = setTimeout(
    () => {
      if (run.stopped) return
      run.refreshing = true
      WebAuthnAbortService.cancelCeremony()
    },
    Math.max(lifetime - REFRESH_EARLY_MS, REFRESH_EARLY_MS),
  )
  try {
    const credential = await startAuthentication({
      optionsJSON: { ...challenge.options, timeout: lifetime },
      useBrowserAutofill: true,
    })
    return await exchangePasskey(challenge, credential)
  } finally {
    clearTimeout(run.timer)
  }
}

interface LoopHooks {
  queryClient: QueryClient
  remember: () => boolean
  onSignedIn: () => void
  onRefused: (error: unknown) => void
}

async function autofillLoop(run: AutofillRun, hooks: LoopHooks): Promise<void> {
  if (!(await browserSupportsWebAuthnAutofill())) return
  let failures = 0
  while (!run.stopped) {
    await whenVisible()
    if (run.stopped) return
    run.refreshing = false
    try {
      const data = await waitForPick(run)
      openPasskeySession(hooks.queryClient, data, hooks.remember())
      hooks.onSignedIn()
      return
    } catch (error) {
      const step = nextAutofillStep(error, run)
      if (step === 'stop') return
      if (step === 'again') {
        if (!run.refreshing) hooks.onRefused(error)
        continue
      }
      failures += 1
      if (failures >= MAX_FAILURES) return
      await sleep(RETRY_MS * failures)
    }
  }
}

/**
 * Offer the console's passkeys in the email field's autofill list.
 *
 * While the sign-in card is idle, a passkey request waits in the background
 * (`useBrowserAutofill`): clicking the email field lists the passkeys this
 * browser holds for the console, and picking one signs in with no button and
 * no password. The request's challenge lives a few minutes, so it is renewed
 * shortly before it runs out — only while the tab is visible, to keep the API's
 * limit for this call free for the admins behind the same address.
 *
 * Pausing (`enabled` false) leaves the waiting request alone: the button or the
 * prompt after a refused password replaces it themselves. Leaving the page
 * cancels it. Returns the last refused pick, if any.
 */
export function usePasskeyAutofill({
  enabled,
  remember,
  onSignedIn,
}: {
  enabled: boolean
  remember: () => boolean
  onSignedIn: () => void
}): unknown {
  const queryClient = useQueryClient()
  const [refused, setRefused] = useState<unknown>(null)
  const readRemember = useEffectEvent(remember)
  const signedIn = useEffectEvent(onSignedIn)

  useEffect(() => {
    if (!enabled) return
    const run: AutofillRun = { stopped: false, refreshing: false }
    void autofillLoop(run, {
      queryClient,
      remember: readRemember,
      onSignedIn: signedIn,
      onRefused: setRefused,
    })
    return () => {
      run.stopped = true
      clearTimeout(run.timer)
    }
  }, [enabled, queryClient])

  useEffect(() => () => WebAuthnAbortService.cancelCeremony(), [])

  return refused
}
